import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import validator from 'validator';
import { Typography, InputPassword, Button, enqueueSnackbar } from '../../../design-system';
import {
  AuthHeaderBase,
  AuthHeaderSubTitle,
  AuthFormBase,
  AuthFooterBase,
  AuthFooterLink,
  AuthFooterLanguage,
} from '../AuthComponents';
import { useCreateNewPasswordMutation, useIsSmallScreen } from '../../../hooks';
import { INPUT_ERRORS, MESSAGES, REGEX } from '../../../const';
import { i18n } from '../../../i18n';

export const CreateNewPasswordHeader = () => {
  return (
    <AuthHeaderBase>
      <Typography variant={'h4'} align={'center'} weight={'bold'}>
        {i18n.t('pages.createNewPassword.title')}
      </Typography>
      <AuthHeaderSubTitle>
        <Typography variant={'textM'} color={'--gray-80'} align="center">
          {i18n.t('pages.createNewPassword.subTitle')}
        </Typography>
      </AuthHeaderSubTitle>
    </AuthHeaderBase>
  );
};

export const CreateNewPasswordForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { mutate } = useCreateNewPasswordMutation();
  const isSmallScreen = useIsSmallScreen();

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);

  const validators = {
    password: password
      ? !validator.isLength(password, { min: 8, max: 16 })
        ? INPUT_ERRORS.NOT_VALID.NEW_PASSWORD_LENGTH
        : !REGEX.PASSWORD.test(password)
        ? INPUT_ERRORS.NOT_VALID.NEW_PASSWORD
        : ''
      : isSubmitClicked
      ? INPUT_ERRORS.REQUIRED.NEW_PASSWORD
      : true,
    passwordConfirm: passwordConfirm
      ? !validator.equals(passwordConfirm, password)
        ? INPUT_ERRORS.NOT_VALID.NEW_PASSWORD_CONFIRM
        : ''
      : isSubmitClicked
      ? INPUT_ERRORS.REQUIRED.NEW_PASSWORD_CONFIRM
      : true,
  };

  const handleSubmitClick = async () => {
    if (!Object.values(validators).every((isError) => !isError)) {
      if (!isSubmitClicked) setIsSubmitClicked(true);
      return;
    }

    await mutate(
      {
        password,
        passwordConfirmation: passwordConfirm,
        token: searchParams.get('passwordResetToken') || '',
      },
      {
        onSuccess: (res) => {
          enqueueSnackbar(res.data.message, 'success');
          navigate('/signin');
        },
        onError: () => {
          enqueueSnackbar(MESSAGES.AUTH.ERROR.NOT_CHANGED_PASSWORD, 'error');
        },
      },
    );
  };

  return (
    <AuthFormBase>
      <InputPassword
        defaultValue={password}
        onChange={(e) => setPassword(e.target.value)}
        label={i18n.t('label.password')}
        placeholder={i18n.t('placeholder.newPassword')}
        data-size={'large'}
        error={(isSubmitClicked && !password) || (!!password && validators.password)}
        helperText={validators.password}
      />
      <InputPassword
        defaultValue={passwordConfirm}
        onChange={(e: any) => setPasswordConfirm(e.target.value)}
        label={i18n.t('label.passwordConfirmation')}
        placeholder={i18n.t('placeholder.passwordConfirmation')}
        data-size={'large'}
        error={
          (isSubmitClicked && !passwordConfirm) || (!!passwordConfirm && validators.passwordConfirm)
        }
        helperText={validators.passwordConfirm}
      />
      <Button data-color={'primary'} size={'large'} onClick={handleSubmitClick}>
        {i18n.t('button.resetPassword')}
      </Button>
      {isSmallScreen && <AuthFooterLanguage />}
    </AuthFormBase>
  );
};

export const CreateNewPasswordFooter = () => {
  return (
    <AuthFooterBase>
      <span />
      <Typography variant={'textS'} color={'--primary-100'}>
        <AuthFooterLink to={'/signin'} withunderline>
          {i18n.t('button.backToSignIn')}
        </AuthFooterLink>
      </Typography>
    </AuthFooterBase>
  );
};
